/**
 * Recognises the CefSharp host-object bridge failure emitted by EMBEDDED
 * BROWSER CRAWLERS — overwhelmingly Microsoft Outlook's Safe Links scanner —
 * NOT by Panda code, so Sentry can drop it before it files as a shell crash.
 *
 * Outlook (and other enterprise link scanners, security crawlers and content
 * indexers) pre-renders links in an embedded Chromium hosted by CefSharp, a
 * .NET wrapper around the Chromium engine. CefSharp exposes .NET objects to
 * page JavaScript through `JavascriptObjectRepository`, and the injected
 * bridge resolves each call by numeric object id. When the crawler tears the
 * page down — or the host object was never registered for that frame — the
 * bridge's promise rejects with a bare STRING:
 *
 *   Object Not Found Matching Id:3, MethodName:update, ParamCount:4
 *
 * Nothing awaits that promise, so it surfaces as an unhandled rejection and
 * Sentry wraps the non-Error value:
 *
 *   UnhandledRejection: Non-Error promise rejection captured with value:
 *     Object Not Found Matching Id:3, MethodName:update, ParamCount:4
 *
 * The `update` in `MethodName:update` is CefSharp's own bound-object method,
 * NOT a Panda update call, and the id is a CefSharp object id, NOT anything in
 * our runtime. Panda is a pure web app: it registers no CefSharp host objects
 * and holds no numeric object registry, so this string can only originate in
 * the crawler's injected bridge. No real user session can produce it, and no
 * Panda code path can be changed to prevent it (Sentry JAVASCRIPT-NEXTJS-5).
 *
 * The match deliberately requires the full CefSharp triple — the id phrase AND
 * `MethodName:` AND `ParamCount:` — so a genuine Panda rejection that happens
 * to mention a missing object is never silenced by accident.
 *
 * Pure and dependency-free (no Sentry import) so it is trivially unit-testable
 * and reusable on any capture path. It accepts the minimal Sentry event shape
 * it inspects; a real `@sentry/nextjs` `ErrorEvent` satisfies it structurally.
 * Mirrors `dom-mutation-noise.ts` / `in-app-browser-noise.ts`.
 */

export const EMBEDDED_CRAWLER_ORIGIN = "embedded-browser-crawler" as const;

interface CrawlerNoiseExceptionValue {
  type?: string;
  value?: string;
}

export interface EmbeddedCrawlerNoiseEvent {
  /** Sentry's `Event.message` (string in practice; typed loose for safety). */
  message?: unknown;
  exception?: { values?: CrawlerNoiseExceptionValue[] };
}

// The CefSharp bridge signature. All three parts are required, in order:
// the numeric object id, the bound method name, and the argument count.
// `[\s\S]*` bridges them across the "Non-Error promise rejection captured
// with value: " prefix Sentry adds and any host-specific wording between.
const CEFSHARP_BRIDGE_RE =
  /Object Not Found Matching Id:\s*\d+[\s\S]*MethodName:[\s\S]*ParamCount:\s*\d+/i;

function matchesEmbeddedCrawler(text: string): boolean {
  return CEFSHARP_BRIDGE_RE.test(text);
}

/**
 * True when the event is embedded-crawler host-bridge noise (Outlook Safe
 * Links and friends) that should be dropped from error reporting.
 */
export function isEmbeddedCrawlerNoise(
  event: EmbeddedCrawlerNoiseEvent | null | undefined,
): boolean {
  if (!event) return false;

  for (const value of event.exception?.values ?? []) {
    const combined = `${value.type ?? ""}: ${value.value ?? ""}`;
    if (matchesEmbeddedCrawler(combined)) return true;
  }

  // Fallback for events carrying only a top-level message (no exception values).
  const message = typeof event.message === "string" ? event.message : "";
  return matchesEmbeddedCrawler(message);
}
